import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  RefreshControl,
  ActivityIndicator,
  Text,
  StatusBar,
} from 'react-native';
import { showAlert } from '@/utils/common/alert';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Icon from 'react-native-vector-icons/Ionicons';
import { useAppSelector } from '@/store';
import { BulletinListItem } from '@/components/residents/bulletins/BulletinListItem';
import { PADDING, ICON_SIZES } from '@/constants/residents/homeConstants';
import { useThemedStyles, useThemeColors } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { notificationStrings } from '@/constants/residents/strings';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { Bulletin } from '@/types/residents/bulletin';
import { useResidentBulletins } from '@/hooks/common/useResidentBulletins';
import { BulletinsListSkeleton } from './BulletinsListSkeleton';

type RootNavigationProp = StackNavigationProp<RootStackParamList>;

export const BulletinsListScreen: React.FC = () => {
  const styles = useThemedStyles(makeStyles);
  const { colors, semantic } = useThemeColors();
  const navigation = useNavigation<RootNavigationProp>();
  const { user } = useAppSelector((state) => state.auth);
  
  const [isRefreshing, setIsRefreshing] = useState(false);

  // iOS-style error handling function (matching UIViewController+Extensions showError)
  const showError = useCallback((error: Error | string, onRetry?: () => void) => {
    const errorMessage = typeof error === 'string' ? error : error.message;
    
    const buttons: Array<{text: string; style?: 'default' | 'cancel' | 'destructive'; onPress?: () => void}> = [
      {
        text: 'OK', // iOS: "alert.accept".localized
        style: 'default',
      }
    ];
    
    // Add retry option for network-related errors
    if (onRetry && (errorMessage.toLowerCase().includes('network') || errorMessage.toLowerCase().includes('timeout'))) {
      buttons.unshift({
        text: 'Retry',
        style: 'default',
        onPress: onRetry,
      });
    }
    
    showAlert('Error', errorMessage);
    
    // If retry callback provided, we could enhance showAlert in the future to support action buttons
    // For now, user can retry by pulling to refresh
  }, []);

  // Memoize the error handler to prevent recreation on every render
  const handleError = useCallback((err: unknown, retry: () => void) => {
    showError(err as Error, () => retry());
  }, [showError]);

  // Memoize the entire options object to prevent hook re-renders
  const bulletinOptions = React.useMemo(() => ({
    propertyId: user?.propertyId,
    onError: handleError,
    pageSize: 25,
    throttleMs: 500,
  }), [user?.propertyId, handleError]);

  const { items: allBulletins, isFetching, isLoading, hasMore, reload, loadNext } = useResidentBulletins(bulletinOptions);

  // Note: Initial loading is handled by useResidentBulletins hook automatically when propertyId changes

  const handleRefresh = useCallback(async () => {
    if (!user?.propertyId) return;
    setIsRefreshing(true);
    await reload();
    setIsRefreshing(false);
  }, [user?.propertyId, reload]);

  const handleLoadMore = useCallback(async () => {
    // Prevent loading more during initial load or when no data yet
    if (!hasMore || !user?.propertyId || isLoading || isFetching || allBulletins.length === 0) {
      return;
    }
    await loadNext();
  }, [hasMore, user?.propertyId, loadNext, isLoading, isFetching, allBulletins.length]);

  const handleBulletinPress = useCallback((bulletin: Bulletin) => {
    navigation.navigate('BulletinDetail', { bulletin });
  }, [navigation]);

  const renderBulletinItem = ({ item }: { item: Bulletin }) => (
    <BulletinListItem 
      bulletin={item} 
      onPress={handleBulletinPress}
    />
  );

  const renderListFooter = () => {
    if (!isFetching) return null;
    
    return (
      <View style={styles.loadingFooter}>
        <ActivityIndicator size="large" color={semantic.text.link} />
      </View>
    );
  };

  const renderEmptyState = () => {
    if (isLoading || isFetching) {
      return <BulletinsListSkeleton />;
    }
    return (
      <View style={styles.emptyContainer}>
        {/* Bell icon matching iOS .Icon.notifications */}
        <View style={styles.emptyIconContainer}>
          <Icon
            name="notifications-outline"
            size={ICON_SIZES.jumbo}
            color={colors.text.muted}
            style={styles.emptyIcon}
          />
        </View>
        <Text style={styles.emptyTitle}>{notificationStrings.empty.title}</Text>
        <Text 
          style={styles.emptyDescription}
          numberOfLines={0} // iOS: numberOfLines = 0 (unlimited lines)
        >
          {notificationStrings.empty.description}
        </Text>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <StatusBar barStyle="dark-content" />
      
      <FlatList
        data={allBulletins}
        renderItem={renderBulletinItem}
        keyExtractor={(item) => item.id.toString()}
        style={styles.list}
        contentContainerStyle={[
          styles.listContent,
          allBulletins.length === 0 && styles.emptyListContent
        ]}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={semantic.text.link}
          />
        }
        onEndReached={allBulletins.length > 0 ? handleLoadMore : undefined}
        onEndReachedThreshold={0.3}
        ListFooterComponent={renderListFooter}
        ListEmptyComponent={renderEmptyState}
        showsVerticalScrollIndicator={false}
        // iOS-like scroll behavior
        scrollEventThrottle={16}
        decelerationRate="normal"
      />
    </View>
  );
};

const makeStyles = ({ colors }: ThemeContextValue) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg.base,
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingTop: PADDING.padding4,
    paddingBottom: PADDING.padding6,
  },
  emptyListContent: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  loadingFooter: {
    height: 50,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: PADDING.padding2,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: PADDING.padding6,
  },
  emptyIconContainer: {
    marginBottom: PADDING.padding4, // iOS spacing after icon
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyIcon: {
    // Icon styling matches iOS gray tint and jumbo size
  },
  emptyTitle: {
    fontSize: 14, // iOS: .avenir900(withSize: 14)
    fontWeight: '900', // iOS: avenir900 (black weight)
    color: colors.text.primary,
    marginBottom: PADDING.padding1, // Tighter spacing like iOS
    textAlign: 'center',
  },
  emptyDescription: {
    fontSize: 13, // iOS: .avenir400(withSize: 13)
    fontWeight: '400', // iOS: avenir400 (regular weight)
    color: colors.text.muted, // iOS: .Text.profileSubtitle
    textAlign: 'center',
    lineHeight: 18, // Appropriate line height for 13px font
  },
});